import {
  input,
  Type,
  DestroyRef,
  Directive,
  inject,
  output,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Observable, of, switchMap, tap } from 'rxjs';

import { EventEmitterLike, InputSignalLike } from '@core/tellent-types';
import type { DialogComponentI, DialogConfigOptions, DialogRef } from '@core/user-interface-legacy';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

@Directive({
  standalone: true,
})
export abstract class DialogWrapComponent<
  C extends DialogComponentI<any, any>,
  O = C extends DialogComponentI<any, infer Result> ? Result : never,
> {
  protected readonly defaultConfig: DialogConfigOptions = {
    width: DialogWidth.Small,
  };
  public open: InputSignalLike<boolean> = input(false);
  public afterClosed: EventEmitterLike<O> = output<O>();

  protected abstract config: DialogConfigOptions;
  protected abstract component: Type<C>;
  protected dialogService = inject(DialogService);
  protected vcRef = inject(ViewContainerRef);
  protected destroyRef = inject(DestroyRef);
  protected dialogRef?: DialogRef;
  private openingInProgress = false;

  public ngOnChanges() {
    this.open() ? this.openDialog() : this.closeDialog();
  }

  public async resolveDialogData(): Promise<any> {
    return Promise.resolve(undefined);
  }

  /**
   * Last chance to act on the dialog's result before the host sees it — override to run the request
   * the confirmation stands for, so the host is told the outcome rather than the answer. Emits
   * whatever this returns; the default passes the result through untouched.
   */
  protected mapResult(result: unknown): Observable<O> {
    return of(<O>result);
  }

  protected async openDialog() {
    if (this.dialogRef || this.openingInProgress) return;
    this.openingInProgress = true;
    const config = {
      ...this.defaultConfig,
      ...this.config,
    };
    const dialogData = await this.resolveDialogData();
    this.dialogRef = this.dialogService.openCustom(
      <Type<DialogComponentI<any, any>>>this.component,
      {
        ...config,
        viewContainerRef: this.vcRef,
      },
      dialogData,
    );
    this.openingInProgress = false;
    this.dialogRef.afterClosed
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.dialogRef = undefined;
        }),
        switchMap(value => this.mapResult(value)),
      )
      .subscribe(value => this.afterClosed.emit(value));
  }

  protected closeDialog() {
    if (!this.dialogRef) return;
    this.dialogRef?.close();
    this.dialogRef = undefined;
  }

  protected ngOnDestroy() {
    this.closeDialog();
  }
}
